import type { Person } from '@local/common-lib';
import {
  Map as IncidentsIcon,
  Business as OrganizationIcon,
  History as PastPatrolsIcon,
  AssignmentInd as PeopleIcon,
  AccountCircle as ProfileIcon,
  TableChart as ReportsIcon,
  DateRange as ScheduledPatrolsIcon,
  Security as SecurityIcon,
  DirectionsCar as VehiclesIcon,
} from '@mui/icons-material';
import { Divider } from '@mui/material';
import type { FC } from 'react';
import type { MenuItemProps } from './components/menu-item/index.js';
import { MenuItem } from './components/menu-item/index.js';
import type { UserInfo } from './utils/auth/index.js';
import { useAuthenticated } from './utils/auth/index.js';

const evaluateMenuItems = (
  userInfo: UserInfo,
  items: [MenuItemProps, Person.Role[]][],
): JSX.Element[] => {
  return items
    .filter((item) => userInfo.hasRole(...item[1]))
    .map((item) => <MenuItem {...item[0]} key={item[0].route} />);
};

export const MenuItems: FC = () => {
  const { userInfo } = useAuthenticated();
  if (userInfo.isSuperAdmin) {
    return (
      <>
        <MenuItem icon={<OrganizationIcon />} label="Organizations" route="/" />
        <Divider />
        <MenuItem icon={<SecurityIcon />} label="Security" route="/security" />
      </>
    );
  }

  return (
    <>
      {evaluateMenuItems(userInfo, [
        [
          { icon: <ScheduledPatrolsIcon />, label: 'My Roster', route: '/' },
          ['patroller'],
        ],
        [
          { icon: <ProfileIcon />, label: 'My Profile', route: '/profile' },
          ['admin', 'watcher', 'summary-recipient', 'patroller'],
        ],
        [
          {
            icon: <ScheduledPatrolsIcon />,
            label: 'Scheduled Patrols',
            route: '/patrols/scheduled',
          },
          ['admin', 'watcher', 'summary-recipient', 'patroller'],
        ],
        [
          {
            icon: <PastPatrolsIcon />,
            label: 'Past Patrols',
            route: '/patrols/past',
          },
          ['admin', 'watcher', 'summary-recipient', 'patroller'],
        ],
        [
          {
            icon: <IncidentsIcon />,
            label: 'Activity Map',
            route: '/activity-map',
          },
          ['admin', 'watcher', 'summary-recipient', 'patroller'],
        ],
        [
          { icon: <PeopleIcon />, label: 'People', route: '/people' },
          ['admin', 'watcher'],
        ],
        [
          { icon: <ReportsIcon />, label: 'Reports', route: '/reports' },
          ['admin'],
        ],
      ])}
      <Divider />
      {evaluateMenuItems(userInfo, [
        [
          {
            icon: <OrganizationIcon />,
            label: 'My Organization',
            route: '/organization',
          },
          ['admin'],
        ],
      ])}
      {evaluateMenuItems(userInfo, [
        [
          {
            icon: <VehiclesIcon />,
            label: 'Vehicles',
            route: '/vehicles',
          },
          ['admin'],
        ],
      ])}
      <MenuItem icon={<SecurityIcon />} label="Security" route="/security" />
    </>
  );
};
